import { useCanvasStore } from '@/stores/canvas'
import { useUndoRedoStore, registerService, unregisterService } from '@/stores/undoRedo'
import { useEditorStore } from '@/stores/editor'
import { useBrandStore } from '@/stores/brand'
import { usePlacementStore } from '@/stores/placement'
import EditorService from '@/services/editorService'
import { addAIShadow, createContactShadow, generateAIShadows, isFlatBase, syncAIShadow, syncShadow } from '@/helpers/shadowHelper'
import type { Logo } from '@/types/logo'
import { TEXT_TYPE_NAMES } from '@/types/textTypeName'
import { handleUSToUKEnglish, USkeywords } from '@/helpers/textHelper'
import type { backdrop } from '@/types/backdrop'
import type { background } from '@/types/background'
import type { AIShadowProperties } from '@/types/aiShadows'
import { autofixOverlays } from '@/helpers/backgroundHelper'
import { useAutofixStore } from '@/stores/autofix'
import { PLACEMENT_TYPES } from '@/constants/positions'

interface ObjectState {
  layerName: string
  left: number
  top: number
  scaleX: number
  scaleY: number
  angle: number
  skewX?: number
  skewY?: number
  originX: string
  originY: string
  // Text-specific properties
  width?: number
  height?: number
  fontSize?: number
  maxLines?: number
  previousHeight?: number,
  index?: number
  aiShadowProperties?: AIShadowProperties
}

class UndoRedoService {
  private readonly canvasStore: ReturnType<typeof useCanvasStore>
  private readonly undoRedoStore: ReturnType<typeof useUndoRedoStore>
  private readonly editorStore: ReturnType<typeof useEditorStore>
  private readonly brandStore: ReturnType<typeof useBrandStore>
  private readonly placementStore: ReturnType<typeof usePlacementStore>
  private readonly autofixStore: ReturnType<typeof useAutofixStore>
  private readonly editorService: EditorService
  private objectStatesBeforeTransform: Map<string, ObjectState> = new Map()

  constructor() {
    this.canvasStore = useCanvasStore()
    this.undoRedoStore = useUndoRedoStore()
    this.editorStore = useEditorStore()
    this.brandStore = useBrandStore()
    this.placementStore = usePlacementStore()
    this.editorService = new EditorService()
    this.autofixStore = useAutofixStore()

    // Register services with the undo/redo store
    registerService('editorService', this.editorService)
    registerService('undoRedoService', this)
  }

  // Clean up method to unregister services
  destroy() {
    this.removeCanvasTransformationTracking()
    unregisterService('editorService')
    unregisterService('undoRedoService')
  }

  updateDiscountChangesWithUndo = async (previousDiscountInfo: any, currentDiscountInfo: any) => {

    // 1. Generate new AI text for both subheadline and headline (based on discount info)
    await this.editorService.generateDiscountText(TEXT_TYPE_NAMES.SUBHEADLINE)
    await this.editorService.generateDiscountText(TEXT_TYPE_NAMES.HEADLINE)

    // 2. Get current headline and subheadline objects from the canvas
    const fabricObjects = this.canvasStore.fabricCanvas.getObjects()
    const subheadlineObj = fabricObjects.find(
      (obj: any) => obj.layerName === TEXT_TYPE_NAMES.SUBHEADLINE,
    )
    const headlineObj = fabricObjects.find((obj: any) => obj.layerName === TEXT_TYPE_NAMES.HEADLINE)

    // If headline object (mandatory) is missing, or subheadline object is missing, stop
    if (!headlineObj || !subheadlineObj) return

    // 3. Capture current text (previous state) and new AI-generated text
    const prevHeadline = headlineObj.text || ''
    const newHeadline = this.editorStore.headline.trim()
    const prevSubheadline = subheadlineObj.text || ''
    const newSubheadline = this.editorStore.subheadline.trim()

    // 4. Do nothing if both headline and subheadline are unchanged
    if (prevHeadline === newHeadline && prevSubheadline === newSubheadline) {
      return
    }

    // 5. Record undo/redo actions so user can revert or reapply the changes
    this.undoRedoStore.recordMultiple(
      `Update Discount ${currentDiscountInfo.type}`,
      [
        // New state
        {
          target: 'editorService',
          method: 'updateCanvasJsonText',
          args: [newSubheadline, TEXT_TYPE_NAMES.SUBHEADLINE],
        },
        {
          target: 'editorService',
          method: 'updateCanvasJsonText',
          args: [newHeadline, TEXT_TYPE_NAMES.HEADLINE, { discountInfo: currentDiscountInfo }],
        },
      ],
      [
        // Previous state
        {
          target: 'editorService',
          method: 'updateCanvasJsonText',
          args: [prevSubheadline, TEXT_TYPE_NAMES.SUBHEADLINE],
        },
        {
          target: 'editorService',
          method: 'updateCanvasJsonText',
          args: [prevHeadline, TEXT_TYPE_NAMES.HEADLINE, { discountInfo: previousDiscountInfo }],
        },
      ],
      `updateDiscount`, // debounce key
      0, // no debounce delay
    )

    // 6. Apply new text changes directly on the canvas
    this.editorService.updateCanvasJsonText(newSubheadline, TEXT_TYPE_NAMES.SUBHEADLINE)
    this.editorService.updateCanvasJsonText(newHeadline, TEXT_TYPE_NAMES.HEADLINE)
    this.canvasStore.fabricCanvas.requestRenderAll()
  }

  updateTextWithUndo = (text: string, layerName: string) => {
    const fabricObjects = this.canvasStore.fabricCanvas.getObjects()
    const layerObj = fabricObjects.find((obj: any) => obj.layerName === layerName)
    if (!layerObj) return

    // Capture current state for undo
    const previousText = layerObj.text || ''
    const newText = text.trim()

    // Only proceed if text actually changed
    if (previousText === newText) {
      return
    }

    // Record the undo/redo action
    this.undoRedoStore.record(
      `Update ${layerName} text`,
      {
        target: 'editorService',
        method: 'updateCanvasJsonText',
        args: [newText, layerName],
      },
      {
        target: 'editorService',
        method: 'updateCanvasJsonText',
        args: [previousText, layerName],
      },
      `updateText_${layerName}`, // debounce key
      0, // debounce for 500ms
    )

    // Perform the actual update
    this.editorService.updateCanvasJsonText(newText, layerName)
    this.canvasStore.fabricCanvas.requestRenderAll()
  }

  updateLogoWithUndo = async (logo: Logo) => {
    const prevLogo = this.brandStore.selectedLogo
    this.undoRedoStore.record(
      `Update logo`,
      { target: 'editorService', method: 'updateLogo', args: [logo] },
      { target: 'editorService', method: 'updateLogo', args: [prevLogo] },
      'updateLogo',
      0,
    )
    await this.editorService.updateLogo(logo)
  }

  // Advanced Settings Undo/Redo Methods

  updateShadowBrightnessWithUndo = (increase: boolean) => {
    // Capture current state before change
    this.undoRedoStore.record(
      `${increase ? 'Increase' : 'Decrease'} shadow brightness`,
      { target: 'editorService', method: 'updateShadowBrightness', args: [increase] },
      { target: 'editorService', method: 'updateShadowBrightness', args: [!increase] },
      'updateShadowBrightness',
      100,
    )

    this.editorService.updateShadowBrightness(increase)
  }

  handleProductTiltWithUndo = async (strategy: any, increase: boolean) => {
    this.undoRedoStore.record(
      `${increase ? 'Increase' : 'Decrease'} product tilt`,
      { target: 'editorService', method: 'handleProductTilt', args: [strategy, increase] },
      { target: 'editorService', method: 'handleProductTilt', args: [strategy, !increase] },
      `productTilt_${strategy.name}`,
      200,
    )

    await this.editorService.handleProductTilt(strategy, increase)
  }

  scaleProductsWithUndo = async (increase: boolean) => {
    this.undoRedoStore.record(
      `${increase ? 'Increase' : 'Decrease'} product size`,
      { target: 'editorService', method: 'scaleProducts', args: [increase] },
      { target: 'editorService', method: 'scaleProducts', args: [!increase] },
      'scaleProducts',
      200,
    )
    await this.editorService.scaleProducts(increase)
  }

  updateShadowLightingWithUndo = (left: boolean) => {
    this.undoRedoStore.record(
      `Update shadow lighting`,
      { target: 'editorService', method: 'updateShadowLighting', args: [left] },
      { target: 'editorService', method: 'updateShadowLighting', args: [!left] },
      'updateShadowLighting',
      100,
    )

    this.editorService.updateShadowLighting(left)
  }

  applyGlowShadowWithUndo = (removeShadow: boolean, newColor?: string, oldColor?: string) => {
    if (this.editorStore.isGlowShadowTriggeredFromUndo) {
      this.editorStore.setIsGlowShadowTriggeredFromUndo(false)
    } else {
      this.undoRedoStore.record(
        `${removeShadow ? 'Remove' : 'Apply'} glow shadow`,
        {
          target: 'editorService',
          method: 'applyGlowShadow',
          args: [removeShadow, newColor, true],
        },
        oldColor && oldColor.length > 0
          ? {
            target: 'editorService',
            method: 'applyGlowShadow',
            args: [removeShadow, oldColor, true],
          }
          : {
            target: 'editorService',
            method: 'applyGlowShadow',
            args: [!removeShadow, undefined, true],
          },
        'applyGlowShadow',
        500,
      )

      this.editorService.applyGlowShadow(removeShadow, newColor, false)
    }
  }

  changeTiltStrategyWithUndo = (strategy: any) => {
    const previousStrategy = this.editorStore.selectedTiltStrategy

    this.undoRedoStore.record(
      `Change tilt strategy to ${strategy.name}`,
      { target: 'editorService', method: 'changeTiltStrategy', args: [strategy] },
      { target: 'editorService', method: 'changeTiltStrategy', args: [previousStrategy] },
      'changeTiltStrategy',
      0,
    )

    this.editorService.changeTiltStrategy(strategy)
  }

  expandDropShadowWithUndo = (increase: boolean) => {
    this.undoRedoStore.record(
      `${increase ? 'Expand' : 'Contract'} drop shadow`,
      { target: 'editorService', method: 'expandDropShadow', args: [increase] },
      { target: 'editorService', method: 'expandDropShadow', args: [!increase] },
      'expandDropShadow',
      100,
    )

    this.editorService.expandDropShadow(increase)
  }

  // Shuffle Undo/Redo Support
  changeRandomShadowWithUndo = async (chooseRandom: boolean = true) => {
    try {
      // Show loading state
      this.canvasStore.setCanvasLoader(true)

      // Capture complete state before shuffle
      const stateBeforeShuffle = this.captureCompleteState()

      // Perform the shuffle
      await this.editorService.changeRandomShadow(chooseRandom)

      // Capture complete state after shuffle
      const stateAfterShuffle = this.captureCompleteState()

      this.undoRedoStore.record(
        'Shuffle placement and content',
        { target: 'undoRedoService', method: 'restoreCompleteState', args: [stateAfterShuffle] },
        { target: 'undoRedoService', method: 'restoreCompleteState', args: [stateBeforeShuffle] },
        'shuffle',
        0,
      )
    } catch (error) {
      console.error('Error in shuffle undo/redo:', error)
      this.canvasStore.showSnackbarMessage('Failed to shuffle with undo support', 'error')
    } finally {
      this.canvasStore.setCanvasLoader(false)
    }
  }

  // Capture complete state for shuffle undo/redo
  private captureCompleteState = () => {
    // Ensure canvas is available
    if (!this.canvasStore.fabricCanvas) {
      throw new Error('Canvas not available for state capture')
    }
    const canvasJson = this.editorService.getCanvasJsonWithCustomInfo(this.canvasStore.fabricCanvas)

    return {
      // Placement state
      placement: this.placementStore.selectedPlacementOption,
      lightDirection: this.placementStore.defaultPlacement?.lightDirection,

      // Background state
      selectedBackgroundOption: this.editorStore.selectedBackgroundOption,
      selectedBackDropIndex: this.editorStore.selectedBackDropIndex,
      selectedBackGroundIndex: this.editorStore.selectedBackGroundIndex,

      // Text content
      headline:
        this.canvasStore.fabricCanvas.getObjects().find((obj: any) => obj.layerName === 'Headline')
          ?.text || '',
      subheadline:
        this.canvasStore.fabricCanvas
          .getObjects()
          .find((obj: any) => obj.layerName === 'SubHeadline')?.text || '',

      // AI-generated content
      aiGeneratedHeadlines: [...this.canvasStore.aiGeneratedHeadlines],
      aiGeneratedSubHeadlines: [...this.canvasStore.aiGeneratedSubHeadlines],

      // Logo state
      selectedLogo: this.brandStore.selectedLogo,

      // Canvas state (positions, angles, etc.)
      canvasJson: canvasJson,

      // Editor state
      shadowOpacity: this.editorStore.shadowOpacity,
      shadowScale: this.editorStore.shadowScale,
      shadowLightingIndex: this.editorStore.shadowLightingIndex,
      isGlowShadowApplied: this.editorStore.isGlowShadowApplied,
      glowColor: this.editorStore.glowColor,
      groupScaleX: this.editorStore.groupScaleX,
      groupScaleY: this.editorStore.groupScaleY,
      selectedTiltStrategy: this.editorStore.selectedTiltStrategy,
    }
  }

  // Restore complete state for shuffle undo/redo
  public restoreCompleteState = async (state: any) => {
    try {
      this.editorStore.isShuffleLoading = true
      // Show loading state
      this.canvasStore.setCanvasLoader(true)
      this.canvasStore.setHideLoader(false)
      // Ensure canvas is available
      if (!this.canvasStore.fabricCanvas) {
        throw new Error('Canvas not available for state restoration')
      }
      // Restore placement
      if (state.placement !== this.placementStore.selectedPlacementOption) {
        await this.editorService.handlePlacementChange(
          state.placement,
          0,
          '',
          false,
          state.selectedBackgroundOption,
          state.selectedBackgroundOption === 0
            ? state.selectedBackGroundIndex
            : state.selectedBackDropIndex,
        )
      }

      // Restore light direction
      if (this.placementStore.defaultPlacement && state.lightDirection) {
        this.placementStore.defaultPlacement.lightDirection = state.lightDirection
      }

      // Restore background/backdrop selection
      if (state.selectedBackgroundOption === 0) {
        if (state.selectedBackGroundIndex !== null) {
          if (state.selectedBackGroundIndex !== this.editorStore.selectedBackGroundIndex) {
            await this.editorService.selectNewBackground(state.selectedBackGroundIndex, true)
          }
        } else {
          await this.editorService.selectNewBackDrop(state.selectedBackDropIndex)
        }
      } else if (state.selectedBackgroundOption === 1) {
        if (state.selectedBackDropIndex !== null) {
          if (state.selectedBackDropIndex !== this.editorStore.selectedBackDropIndex) {
            await this.editorService.selectNewBackDrop(state.selectedBackDropIndex)
          }
        } else {
          await this.editorService.selectNewBackground(state.selectedBackGroundIndex, true)
        }
      }

      // Restore AI-generated content first (so text restoration can use them)
      if (state.aiGeneratedHeadlines) {
        this.canvasStore.setAIGeneratedHeadlines(state.aiGeneratedHeadlines)
      }
      if (state.aiGeneratedSubHeadlines) {
        this.canvasStore.setAIGeneratedSubHeadlines(state.aiGeneratedSubHeadlines)
      }

      // Restore canvas state
      if (state.canvasJson) {
        // Use the proper canvas loading helper that handles post-processing
        this.editorStore.setUpdatedCanvasData(state.canvasJson)
        this.editorStore.setShouldUpdateCanvasData(true)
      }

      // Restore logo
      if (state.selectedLogo && state.selectedLogo !== this.brandStore.selectedLogo) {
        await this.editorService.updateLogo(state.selectedLogo)
      }

      // Restore editor state
      if (state.shadowOpacity !== undefined) {
        this.editorStore.setShadowOpacity(state.shadowOpacity)
      }
      if (state.shadowScale !== undefined) {
        this.editorStore.setShadowScale(state.shadowScale)
      }
      if (state.shadowLightingIndex !== undefined) {
        this.editorStore.setShadowLightingIndex(state.shadowLightingIndex)
      }
      if (state.isGlowShadowApplied !== undefined) {
        this.editorStore.setIsGlowShadowApplied(state.isGlowShadowApplied)
      }
      if (state.glowColor !== undefined) {
        this.editorStore.setGlowColor(state.glowColor)
      }
      if (state.groupScaleX !== undefined) {
        this.editorStore.setGroupScaleX(state.groupScaleX)
      }
      if (state.groupScaleY !== undefined) {
        this.editorStore.setGroupScaleY(state.groupScaleY)
      }
      if (state.selectedTiltStrategy) {
        this.editorStore.setSelectedTiltStrategy(state.selectedTiltStrategy)
      }
    } catch (error) {
      console.error('Error restoring complete state:', error)
      this.canvasStore.showSnackbarMessage('Failed to restore previous state', 'error')
    } finally {
      this.canvasStore.setCanvasLoader(false)
      this.canvasStore.setHideLoader(true)
      this.editorStore.isShuffleLoading = false
    }
  }

  // Background-related Undo/Redo Methods

  addBackDropsWithUndo = async (
    backdrop: backdrop,
    action: string = 'append',
  ) => {
    // Capture the current backdrop count to determine the index where the new backdrop will be added
    const currentBackDropCount = this.editorStore.createdBackDrops.length
    const newIndex = action === 'prepend' ? 0 : currentBackDropCount

    this.undoRedoStore.record(
      `Add backdrop`,
      {
        target: 'editorService',
        method: 'addBackDrops',
        args: [backdrop, action],
      },
      {
        target: 'editorService',
        method: 'removeBackDrops',
        args: [newIndex],
      },
      'addBackDrops',
      0,
    )

    await this.editorService.addBackDrops(backdrop, action)
  }

  editBackDropWithUndo = async (
    index: number,
    backdrop: {
      backgroundColor: string
      textColor: string
      backgroundTextureIndex: number
    },
  ) => {
    // Capture the previous backdrop state before editing
    const previousBackdrop = this.editorStore.createdBackDrops[index]

    this.undoRedoStore.record(
      `Edit backdrop ${index}`,
      {
        target: 'editorService',
        method: 'editBackDrop',
        args: [index, backdrop],
      },
      {
        target: 'editorService',
        method: 'editBackDrop',
        args: [index, previousBackdrop],
      },
      'editBackDrop',
      0,
    )

    await this.editorService.editBackDrop(index, backdrop)
  }

  addBackGroundsWithUndo = async (
    background: background,
    action: string = 'prepend',
  ) => {
    // Capture the current background count to determine the index where the new background will be added
    const currentBackGroundCount = this.editorStore.createdBackGrounds.length
    const newIndex = action === 'prepend' ? 0 : currentBackGroundCount

    this.undoRedoStore.record(
      `Add background`,
      {
        target: 'editorService',
        method: 'addBackGrounds',
        args: [background, action],
      },
      {
        target: 'editorService',
        method: 'removeBackGrounds',
        args: [newIndex],
      },
      'addBackGrounds',
      0,
    )

    await this.editorService.addBackGrounds(background, action)
  }

  editBackGroundWithUndo = async (
    index: number,
    background: {
      backgroundImageIndex: number
      textColor: string
      backgroundTextureIndex: number | null
    },
  ) => {
    // Capture the previous background state before editing
    const previousBackground = this.editorStore.createdBackGrounds[index]

    this.undoRedoStore.record(
      `Edit background ${index}`,
      {
        target: 'editorService',
        method: 'editBackGround',
        args: [index, background],
      },
      {
        target: 'editorService',
        method: 'editBackGround',
        args: [index, previousBackground],
      },
      'editBackGround',
      0,
    )

    await this.editorService.editBackGround(index, background)
  }

  selectNewBackDropWithUndo = async (index: number) => {
    let previousIndex = this.editorStore.selectedBackDropIndex
    let serviceMethod = 'selectNewBackDrop'

    if (this.editorStore.selectedBackDropIndex === null) {
      previousIndex = this.editorStore.selectedBackGroundIndex
      serviceMethod = 'selectNewBackground'
    }

    this.undoRedoStore.record(
      `Select backdrop ${index}`,
      { target: 'editorService', method: 'selectNewBackDrop', args: [index] },
      { target: 'editorService', method: serviceMethod, args: [previousIndex] },
      `selectBackDrop_${index}`,
      0,
    )

    await this.editorService.selectNewBackDrop(index)
  }

  selectNewBackgroundWithUndo = async (index: number) => {
    let previousIndex = this.editorStore.selectedBackGroundIndex
    let serviceMethod = 'selectNewBackground'

    if (this.editorStore.selectedBackGroundIndex === null) {
      previousIndex = this.editorStore.selectedBackDropIndex
      serviceMethod = 'selectNewBackDrop'
    }

    // Check if we need to remove glow shadow (when switching from backdrop to AI background with CORNER placement)
    const isCornerPlacement = this.placementStore.selectedPlacementOption === PLACEMENT_TYPES.CORNER
    const isGlowShadowApplied = this.editorStore.isGlowShadowApplied
    const needsGlowShadowRemoval = isCornerPlacement && isGlowShadowApplied

    if (needsGlowShadowRemoval) {
      // Combine background selection and glow shadow removal into a single atomic action
      const currentGlowColor = this.editorStore.glowColor

      this.undoRedoStore.recordMultiple(
        `Select background ${index}`,
        [
          { target: 'editorService', method: 'selectNewBackground', args: [index, true] },
        ],
        [
          { target: 'editorService', method: serviceMethod, args: serviceMethod === 'selectNewBackground' ? [previousIndex, true] : [previousIndex] },
          ...(currentGlowColor
            ? [
              {
                target: 'editorService',
                method: 'applyGlowShadow',
                args: [false, currentGlowColor, true],
              },
            ]
            : []),
        ],
        `selectBackground_${index}`,
        0,
      )
    } else {
      // No glow shadow removal needed, use single action
      this.undoRedoStore.record(
        `Select background ${index}`,
        { target: 'editorService', method: 'selectNewBackground', args: [index, true] },
        { target: 'editorService', method: serviceMethod, args: serviceMethod === 'selectNewBackground' ? [previousIndex, true] : [previousIndex] },
        `selectBackground_${index}`,
        0,
      )
    }

    await this.editorService.selectNewBackground(index)
  }

  handlePlacementChangeWithUndo = async (placement: string) => {
    const currentPlacement = this.placementStore.selectedPlacementOption

    let currentAngle = 0
    const product = this.canvasStore.fabricCanvas.getObjects().find((o: any) => o.layerName?.includes('Product'))
    if (product) {
      currentAngle = product.angle
    }

    // Some custom logics to revet bacl to exact backdrop / background
    const selectedBackgroundType = this.placementStore.selectedAIBackgroundImage ? 0 : 1
    let selectedIndex
    if (selectedBackgroundType === 0) {
      selectedIndex = this.editorStore.selectedBackGroundIndex
    } else {
      selectedIndex = this.editorStore.selectedBackDropIndex
    }

    const previousBackdrops = JSON.parse(JSON.stringify(this.editorStore.createdBackDrops))
    const previousBackgrounds = JSON.parse(JSON.stringify(this.editorStore.createdBackGrounds))

    this.undoRedoStore.recordMultiple(
      `Change placement to ${placement}`,
      [
        {
          target: 'editorService',
          method: 'handlePlacementChange',
          args: [placement],
        },
      ],
      [
        {
          target: 'editorService',
          method: 'restoreCreatedBackdrops',
          args: [previousBackdrops],
        },
        {
          target: 'editorService',
          method: 'restoreCreatedBackgrounds',
          args: [previousBackgrounds],
        },
        {
          target: 'editorService',
          method: 'handlePlacementChange',
          args: [currentPlacement, currentAngle, '', false, selectedBackgroundType, selectedIndex],
        },
        ...(this.editorStore.isGlowShadowApplied
          ? [
            {
              target: 'editorService',
              method: 'applyGlowShadow',
              args: [false, this.editorStore.glowColor, true],
            },
          ]
          : [
            {
              target: 'editorService',
              method: 'applyGlowShadow',
              args: [true, this.editorStore.glowColor, true],
            },
          ]),
      ],
      'placementChange',
      0,
    )

    // Execute the change
    await this.editorService.handlePlacementChange(placement)
  }

  selectPlacementAndBackgroundWithUndo = async (placement: string, index: number) => {
    const currentPlacement = this.placementStore.selectedPlacementOption

    // Figure out current background/backdrop index
    const selectedBackgroundType = this.placementStore.selectedAIBackgroundImage ? 0 : 1
    let selectedIndex
    if (this.placementStore.selectedAIBackgroundImage) {
      selectedIndex = this.editorStore.selectedBackGroundIndex
    } else {
      selectedIndex = this.editorStore.selectedBackDropIndex
    }

    const previousBackdrops = JSON.parse(JSON.stringify(this.editorStore.createdBackDrops))
    const previousBackgrounds = JSON.parse(JSON.stringify(this.editorStore.createdBackGrounds))

    // Resolve previous background state
    let previousIndex = this.editorStore.selectedBackGroundIndex
    let serviceMethod = 'selectNewBackground'
    if (this.placementStore.selectedBackgroundImage) {
      previousIndex = this.editorStore.selectedBackDropIndex
      serviceMethod = 'selectNewBackDrop'
    }

    this.undoRedoStore.recordMultiple(
      `Change placement to ${placement} and background to ${index}`,
      [
        // DO actions
        {
          target: 'editorService',
          method: 'handlePlacementChange',
          args: [placement],
        },
        {
          target: 'editorService',
          method: 'selectNewBackground',
          args: [index, true],
        },
      ],
      [
        // UNDO actions
        {
          target: 'editorService',
          method: 'restoreCreatedBackdrops',
          args: [previousBackdrops],
        },
        {
          target: 'editorService',
          method: 'restoreCreatedBackgrounds',
          args: [previousBackgrounds],
        },
        ...(this.editorStore.isGlowShadowApplied
          ? [
            {
              target: 'editorService',
              method: 'applyGlowShadow',
              args: [false, this.editorStore.glowColor, true],
            },
          ]
          : []),
        {
          target: 'editorService',
          method: 'handlePlacementChange',
          args: [currentPlacement, 0, '', false, selectedBackgroundType, selectedIndex],
        },
        {
          target: 'editorService',
          method: serviceMethod,
          args: serviceMethod === 'selectNewBackground' ? [previousIndex, true] : [previousIndex],
        },
      ],
      'placementAndBackgroundChange',
      0,
    )

    // Execute both changes
    await this.editorService.handlePlacementChange(placement)
    await this.editorService.selectNewBackground(index, true)
  }

  selectNewOverlayWithUndo = async (index: number) => {
    const previousIndex = autofixOverlays.findIndex(overlay => overlay === this.placementStore.selectedOverlayImage)
    this.undoRedoStore.record("adding new overlay", { target: 'editorService', method: 'selectNewOverlay', args: [index] }, { target: 'editorService', method: 'selectNewOverlay', args: [previousIndex] }, `selectOverlay_${index}`, 0)

    await this.editorService.selectNewOverlay(index)
  }

  selectNewTextColorWithUndo = async (index: number) => {
    const previousIndex = this.autofixStore.autofixColors.findIndex(textColor => textColor === this.canvasStore.textColor)
    this.undoRedoStore.record("adding new text color", { target: 'editorService', method: 'selectNewTextColor', args: [index] }, { target: 'editorService', method: 'selectNewTextColor', args: [previousIndex] }, `selectText_${index}`, 0)
    this.editorService.selectNewTextColor(index)
  }

  addTextColorWithUndo = async (
    textColor: string,
  ) => {
    // Capture the current backdrop count to determine the index where the new backdrop will be added
    const currentTextIndex = this.autofixStore.autofixColors.findIndex(textColor => textColor === this.canvasStore.textColor)

    this.undoRedoStore.record(
      `Add Text Color`,
      {
        target: 'editorService',
        method: 'addTextColor',
        args: [textColor],
      },
      {
        target: 'editorService',
        method: 'removeTextColor',
        args: [currentTextIndex],
      },
      'addTextColor',
      0,
    )

    await this.editorService.addTextColor(textColor)
  }

  // Helper methods for state management

  private captureCanvasObjectStates = () => {
    const fabricObjects = this.canvasStore.fabricCanvas.getObjects()
    const states: { [layerName: string]: any } = {}

    fabricObjects.forEach((obj: any) => {
      if (obj.layerName && this.isTransformableObject(obj)) {
        states[obj.layerName] = this.captureObjectState(obj)
      }
    })

    return states
  }

  restoreEditorState = (editorState: any) => {
    this.editorStore.updateAdvancedSettingsState(editorState)
  }

  restoreCanvasAndEditorState = (canvasStates: any, editorState: any) => {
    // Restore canvas objects
    Object.keys(canvasStates).forEach((layerName) => {
      this.restoreObjectState(layerName, canvasStates[layerName])
    })

    // Restore editor state
    this.editorStore.updateAdvancedSettingsState(editorState)
  }

  // Object transformation methods
  private captureObjectState = (obj: any): ObjectState => {
    const objects = this.canvasStore.fabricCanvas.getObjects()
    const canvasObject = objects.find((o: { layerName: any }) => o.layerName === obj.layerName)
    const objectIndex = objects.indexOf(canvasObject)
    const baseState: ObjectState = {
      layerName: obj.layerName || '',
      left: obj.left || 0,
      top: obj.top || 0,
      scaleX: obj.scaleX || 1,
      scaleY: obj.scaleY || 1,
      angle: obj.angle || 0,
      skewX: obj.skewX || 0,
      skewY: obj.skewY || 0,
      originX: obj.originX || 'left',
      originY: obj.originY || 'top',
      index: objectIndex,
      aiShadowProperties: obj.aiShadowProperties,
    }

    // Capture text-specific properties for text objects
    if (obj.layerName === 'Headline' || obj.layerName === 'SubHeadline') {
      baseState.width = obj.width
      baseState.height = obj.height
      baseState.fontSize = obj.fontSize
      baseState.maxLines = obj.maxLines
      baseState.previousHeight = obj.previousHeight
    }

    return baseState
  }

  private restoreObjectState = (layerName: string, state: ObjectState) => {
    const fabricObjects = this.canvasStore.fabricCanvas.getObjects()
    const obj = fabricObjects.find((o: any) => o.layerName === layerName)

    if (!obj) return

    // Restore basic transformation properties
    obj.set({
      left: state.left,
      top: state.top,
      scaleX: state.scaleX,
      scaleY: state.scaleY,
      angle: state.angle,
      skewX: state.skewX,
      skewY: state.skewY,
      originX: state.originX,
      originY: state.originY,
      aiShadowProperties: state.aiShadowProperties
    })
    this.canvasStore.fabricCanvas.moveObjectTo(obj, state.index)

    // Restore text-specific properties for text objects
    if ((layerName === 'Headline' || layerName === 'SubHeadline') && state.width !== undefined) {
      obj.set({
        width: state.width,
        height: state.height,
        fontSize: state.fontSize,
        maxLines: state.maxLines,
      })

      // Set previousHeight for text reflow calculations
      if (state.previousHeight !== undefined) {
        obj.previousHeight = state.previousHeight
      }

      // Initialize dimensions for text objects after property changes
      obj.initDimensions()
    }

    obj.setCoords()

    // Sync contact shadow if this object has one
    this.syncObjectShadow(obj)

    // Handle text reflow for text objects
    this.editorService.triggerTextReflow()

    this.canvasStore.fabricCanvas.requestRenderAll()
  }

  private syncObjectShadow = async (obj: any) => {
    // Only sync shadows for product objects
    if (!obj.layerName?.toLowerCase().includes('product') || !obj.layerId) return

    const fabricObjects = this.canvasStore.fabricCanvas.getObjects()
    const shadow = fabricObjects.find(
      (o: any) => o.layerName === 'Shadow' && o.layerId === obj.layerId,
    )

    const aiShadow = fabricObjects.find(
      (o: any) => o.layerName === 'AIShadow' && o.layerId === obj.layerId,
    )

    // Determine shadow type for front placement
    const aspectRatio = (obj.height * obj.scaleY) / (obj.width * obj.scaleX)
    let shadowType
    if (Math.abs(obj.angle % 360) > 20 && Math.abs(obj.angle % 360) < 340) {
      shadowType = 'rotate'
    } else {
      shadowType = aspectRatio > 1.5 ? 'ellipse' : 'square'
    }

    if (isFlatBase(obj.angle)) {
      if (aiShadow) {
        const aiShadowProperties = (aiShadow as any).aiShadowProperties as AIShadowProperties
        syncAIShadow(obj, aiShadow, this.canvasStore.fabricCanvas, aiShadowProperties)
        this.moveShadowBehindProduct(this.canvasStore.fabricCanvas, obj, aiShadow)
      } else if (obj.aiShadowProperties) {
        this.canvasStore.fabricCanvas.remove(shadow)
        addAIShadow(obj, this.canvasStore.fabricCanvas, obj.aiShadowProperties)
      } else {
        const newShadow = await createContactShadow(obj, shadowType, this.canvasStore.fabricCanvas)
        syncShadow(obj, newShadow, this.canvasStore.fabricCanvas)
      }

      return
    }

    if (shadow) {
      syncShadow(obj, shadow, this.canvasStore.fabricCanvas)
    } else {
      this.canvasStore.fabricCanvas.remove(aiShadow)
      const newShadow = await createContactShadow(obj, shadowType, this.canvasStore.fabricCanvas)
      syncShadow(obj, newShadow, this.canvasStore.fabricCanvas)
    }
  }

  private moveShadowBehindProduct = async (canvas: any, product: any, shadow: any) => {
    let productIndex = canvas.getObjects().indexOf(product)
    if (productIndex == -1) {
      const product = canvas.getObjects().find((obj: any) => obj.layerName === product.layerName);
      if (product) {
        productIndex = canvas.getObjects().indexOf(product)
      }
    }
    canvas.moveObjectTo(shadow, productIndex)
    canvas.requestRenderAll()
  }

  private isTransformableObject = (obj: any): boolean => {
    if (!obj.layerName) return false

    // Exclude non-selectable objects (like Logo)
    if (!obj.selectable || !obj.evented) return false

    // Include products, groups, text objects, and other transformable objects
    return (
      obj.layerName.includes('Product') ||
      obj.layerName.includes('glowGroup') ||
      obj.layerName.includes('productsGroup') ||
      obj.layerName === 'Headline' ||
      obj.layerName === 'SubHeadline' ||
      obj.type === 'group'
    )
  }

  setupCanvasTransformationTracking = () => {
    if (!this.canvasStore.fabricCanvas) return

    const canvas = this.canvasStore.fabricCanvas

    canvas.on('mouse:down', (e: any) => {
      this.onTransformStart(e)
    })

    canvas.on('control:clicked', (options: { controlName: string; target: any }) => {
      if (options.controlName === 'moveToFront') {
        this.onTransformStart(options)
      }
      if (options.controlName === 'moveToBottom') {
        this.onTransformStart(options)
      }
    })

    // Single end event for all modifications
    canvas.on('object:modified', this.onTransformEnd)
  }

  // Manual method to track property changes when they happen programmatically (fallback)
  trackObjectPropertyChange = (layerName: string) => {
    const fabricObjects = this.canvasStore.fabricCanvas.getObjects()
    const obj = fabricObjects.find((o: any) => o.layerName === layerName)

    if (!obj || !this.isTransformableObject(obj)) return

    // Capture state before change if not already captured
    if (!this.objectStatesBeforeTransform.has(layerName)) {
      this.objectStatesBeforeTransform.set(layerName, this.captureObjectState(obj))
    }

    // Create a fake event object and trigger the end handler after a short delay
    setTimeout(() => {
      const fakeEvent = { target: obj }
      this.onTransformEnd(fakeEvent)
    }, 100)
  }

  private onTransformStart = (e: any) => {
    const obj = e.target
    if (!obj) return
    if (!this.isTransformableObject(obj)) return

    const layerName = obj.layerName
    if (!this.objectStatesBeforeTransform.has(layerName)) {
      this.objectStatesBeforeTransform.set(layerName, this.captureObjectState(obj))
    }
  }

  private onTransformEnd = (e: any) => {
    const obj = e.target
    if (!this.isTransformableObject(obj)) return

    const layerName = obj.layerName
    const beforeState = this.objectStatesBeforeTransform.get(layerName)

    if (!beforeState) return

    const afterState = this.captureObjectState(obj)

    // Check if state actually changed
    const hasChanged = Object.keys(beforeState).some((key) => {
      const k = key as keyof ObjectState
      return beforeState[k] !== afterState[k]
    })

    if (hasChanged) {
      // Record the transformation for undo/redo
      this.undoRedoStore.record(
        `Transform ${layerName}`,
        {
          target: 'undoRedoService',
          method: 'restoreObjectState',
          args: [layerName, afterState],
        },
        {
          target: 'undoRedoService',
          method: 'restoreObjectState',
          args: [layerName, beforeState],
        },
        `transform_${layerName}`,
        300, // 300ms debounce for smooth grouping
      )
    }

    // Clear the before state
    this.objectStatesBeforeTransform.delete(layerName)
  }

  removeCanvasTransformationTracking = () => {
    if (!this.canvasStore.fabricCanvas) return

    const canvas = this.canvasStore.fabricCanvas

    canvas.off('object:modified', this.onTransformEnd)
    canvas.off('control:clicked')
  }

  resetObjectStateMapping() {
    this.objectStatesBeforeTransform.clear()
  }
}

export default UndoRedoService
